import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { AgGridReact } from '@ag-grid-community/react'
import {
  GridReadyEvent,
  IServerSideGetRowsParams,
  IsFullWidthRowParams,
  RowClassParams,
  RowStyle,
} from '@ag-grid-community/core'
import { Stack } from '@sureco/design-system'
import { getCensusFiles } from '../api/census-enrollment-platform-api-client'
import { CensusFileRowStatusEnum } from '../api/enums/census-file-row-status.enum'
import { SortDirection } from '../../../types/sorted-request.dto'
import {
  CENSUS_MANAGEMENT_PATH,
  DEFAULT_FILES_PAGINATION_LIMIT,
} from '../constants'
import { CensusFileWithRowStatsDto } from '../api/dto/census-file-with-row-stats.dto'
import { CensusFileRowTypeEnum } from '../api/enums/census-file-row-type.enum'
import { useCensusV2Clients } from '../hooks/use-census-v2-clients'
import { useDraftsQueueRows } from '../hooks/useDraftsQueueRows'
import { getCensusFilesColDefs } from '../utils/get-census-files-col-defs'
import { buildCensusUploadQueueFilters } from '../utils/census-upload-job'
import {
  buildDraftsPinnedRows,
  isQueueSummaryRow,
} from '../utils/drafts-queue-rows'
import { withDraftsQueueCells } from '../utils/with-drafts-queue-cells'
import { filterClients } from '../CensusReview/utils/filter-clients'
import { CensusClientFilter } from '../types/census-client-filter'
import { useSelector } from 'react-redux'
import { selectors as authSelectors } from '@hixme/auth-ui'
import { ROLE_PLATFORM_HIXME_ADMIN } from '@hixme/role-policy'
import DraftsQueueSummaryRow from './DraftsQueueSummaryRow'

type Props = {
  clientNameLookup: Record<string, string>
  fileUpdated: boolean
  filter?: CensusClientFilter
  // Pin unfinished uploads (queued, processing, failed) above the files
  showQueueRows?: boolean
  // Brokers only view; the API rejects their Dismiss
  canDismissQueueRows?: boolean
}

// Kept outside render: a new object each render repaints every row while polling
const ROW_STYLE: RowStyle = { backgroundColor: 'white', marginBottom: '20px' }

// Classes, not inline styles: CensusReview.scss forces row backgrounds with !important
const getRowClass = ({ node, data }: RowClassParams): string | undefined => {
  if (!node.rowPinned) {
    return undefined
  }
  return data?.status === 'FAILED'
    ? 'drafts-queue-row-failed'
    : 'drafts-queue-row'
}

const isFullWidthRow = ({ rowNode }: IsFullWidthRowParams): boolean =>
  isQueueSummaryRow(rowNode.data)

// Census Manage)
const NewAndUpdatedRecordsGrid = ({
  clientNameLookup,
  fileUpdated,
  filter,
  showQueueRows = false,
  canDismissQueueRows = false,
}: Props) => {
  const [grid, setGrid] = useState<GridReadyEvent>()
  const [isQueueExpanded, setIsQueueExpanded] = useState(false)
  const { clients } = useCensusV2Clients()
  const role = useSelector(authSelectors.getRole) as string
  // Memoized so polling the queue does not rebuild the datasource and reload Drafts
  const clientIds = useMemo(
    () => filterClients(clients, filter, role).map((client) => client.id),
    [clients, filter, role]
  )
  const queueFilters = useMemo(
    () => buildCensusUploadQueueFilters(clientIds, filter),
    [clientIds, filter]
  )

  const refreshDrafts = useCallback(() => {
    grid?.api.refreshServerSide({ purge: true })
  }, [grid])

  const {
    jobs,
    total,
    refetch: refetchQueue,
  } = useDraftsQueueRows({
    filters: queueFilters,
    refreshKey: fileUpdated,
    enabled: showQueueRows,
    onJobsFinished: refreshDrafts,
  })

  const pinnedTopRowData = useMemo(
    () => buildDraftsPinnedRows({ jobs, total, isExpanded: isQueueExpanded }),
    [jobs, total, isQueueExpanded]
  )

  const datasource = useMemo(
    () => ({
      getRows: async (
        params: IServerSideGetRowsParams<CensusFileWithRowStatsDto>
      ) => {
        const limit = DEFAULT_FILES_PAGINATION_LIMIT
        let sort: string | undefined
        let order: SortDirection | undefined
        try {
          const { startRow, sortModel } = params.request
          if (sortModel.length > 0) {
            const { colId, sort: direction } = sortModel[0] // Take the first sort entry
            sort = colId
            order =
              direction === 'desc' ? SortDirection.desc : SortDirection.asc
          }
          const status = [CensusFileRowStatusEnum.IN_PROCESS]
          // const { }

          const page = Math.floor((startRow ?? 1) / limit) + 1

          const { meta: pagination, data: files } = await getCensusFiles({
            status,
            page,
            limit,
            sort,
            order,
            clientPublicKey: clientIds,
            includeTestClients: filter?.includeTestCompanies,
            dateFrom: filter?.dateFrom,
            dateTo: filter?.dateTo,
          })

          params.success({ rowData: files, rowCount: pagination.total })
        } catch (error) {
          console.error(error)
          params.fail()
        }
      },
    }),
    [clientIds, clientNameLookup, filter]
  )

  useEffect(() => {
    if (grid) {
      grid.api.refreshServerSide({ purge: true })
    }
  }, [fileUpdated, grid])

  useEffect(() => {
    if (grid) {
      grid.api.setGridOption('serverSideDatasource', datasource)
      grid.api.purgeInfiniteCache()
      grid.api.refreshServerSide({ purge: true })
    }
  }, [grid, datasource])

  const clickView = (publicKey: string) => {
    window.open(
      `${CENSUS_MANAGEMENT_PATH}/${publicKey}/${CensusFileRowTypeEnum.DRAFT_EMPLOYEE}`,
      '_blank'
    )
  }

  const columnDefs = withDraftsQueueCells(
    getCensusFilesColDefs({
      hasSubmittedColumn: false,
      clientNameLookup,
      clickView,
      isSurecoAdmin: role === ROLE_PLATFORM_HIXME_ADMIN,
    }),
    {
      clientNameLookup,
      onChanged: refetchQueue,
      canDismiss: canDismissQueueRows,
    }
  )

  const fullWidthCellRendererParams = useMemo(
    () => ({ onToggle: () => setIsQueueExpanded((expanded) => !expanded) }),
    []
  )

  return (
    <>
      <Stack direction="row" align="center" className="subtitle">
        Drafts
      </Stack>
      <AgGridReact
        rowStyle={ROW_STYLE}
        getRowClass={getRowClass}
        className="censusReviewGrid ag-theme-alpine"
        getRowId={({ data }) => data.id}
        pinnedTopRowData={pinnedTopRowData}
        isFullWidthRow={isFullWidthRow}
        fullWidthCellRenderer={DraftsQueueSummaryRow}
        fullWidthCellRendererParams={fullWidthCellRendererParams}
        pagination
        paginationPageSize={10}
        cacheBlockSize={10}
        columnDefs={columnDefs}
        rowModelType="serverSide"
        onGridReady={(params) => setGrid(params)}
        domLayout="autoHeight"
        suppressHorizontalScroll
        paginationPageSizeSelector={[10, 20, 50, 100]}
      />
      <hr className="page-hr" style={{ marginTop: '10px' }} />
    </>
  )
}

export default NewAndUpdatedRecordsGrid
